import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Receipt, Upload, Check, FileText } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getBillingQueue, billItem, billMultiItems, billingShortClose, getFirms } from '../../services/taskApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlowBilling = () => {
  const { isDark } = useOutletContext();
  const [items, setItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [search, setSearch] = useState('');
  const [billing, setBilling] = useState(null); // currently being billed item OR { multi: true, items: [...] }
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ tally_invoice_no: '', invoice_date: '', billed_qty: '', invoice_pdf_url: '' });
  const [selected, setSelected] = useState({}); // item_id -> true (for multi-select)
  const [billedQtys, setBilledQtys] = useState({}); // item_id -> qty

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, fRes] = await Promise.all([getBillingQueue(filterCompany || undefined, search || undefined), getFirms()]);
      setItems(iRes.data || []);
      setFirms(fRes.data || []);
    } catch (err) { console.error('FlowBilling: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterCompany, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pendingToBill = (it) => Math.max(
    (parseInt(it.quantity_allocated_to_bill) || 0) - (parseInt(it.quantity_billed) || 0),
    0
  );

  const openBilling = (item) => {
    setBilling(item);
    setForm({
      tally_invoice_no: '',
      invoice_date: new Date().toISOString().split('T')[0],
      billed_qty: pendingToBill(item),
      invoice_pdf_url: ''
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('task_token');
      const res = await fetch(`${API_URL}/api/tasks-app/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (data.url) {
        setForm(f => ({ ...f, invoice_pdf_url: data.url }));
        toast.success('Invoice uploaded');
      }
    } catch { toast.error('Upload failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tally_invoice_no.trim()) { toast.error('Invoice number is required'); return; }
    if (!form.invoice_date) { toast.error('Invoice date is required'); return; }
    if (!form.invoice_pdf_url) { toast.error('Upload invoice PDF/image'); return; }

    // Pre-flight confirm: recap the invoice details so the user can't submit blindly.
    const itemSummary = billing.multi
      ? billing.items.map(it => {
          const q = parseInt(billedQtys[it.id] ?? pendingToBill(it));
          return `  • ${it.item_name} — ${q} units`;
        }).join('\n')
      : `  • ${billing.item_name} — ${form.billed_qty} units`;
    const confirmMsg =
      `Confirm invoice submission?\n\n` +
      `Invoice #: ${form.tally_invoice_no}\n` +
      `Date: ${form.invoice_date}\n` +
      `Items:\n${itemSummary}\n\n` +
      `Once submitted, this invoice cannot be undone without an admin revert. ` +
      `Verify the invoice number, date, and quantity before confirming.`;
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      if (billing.multi) {
        const itemIds = billing.items.map(i => i.id);
        const qtys = {};
        for (const it of billing.items) {
          const remaining = pendingToBill(it);
          const q = parseInt(billedQtys[it.id] ?? remaining);
          if (!q || q < 1 || q > remaining) {
            toast.error(`${it.item_name}: qty must be 1–${remaining}`); setSubmitting(false); return;
          }
          qtys[it.id] = q;
        }
        await billMultiItems({ item_ids: itemIds, ...form, billed_qtys: qtys });
        toast.success(`${itemIds.length} items billed`);
      } else {
        const remaining = pendingToBill(billing);
        const qty = parseInt(form.billed_qty);
        if (!qty || qty < 1 || qty > remaining) {
          toast.error(`Billed qty must be 1–${remaining}`); setSubmitting(false); return;
        }
        await billItem(billing.id, { ...form, billed_qty: qty });
        toast.success('Item billed successfully');
      }
      setBilling(null);
      setSelected({});
      setBilledQtys({});
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // Group by SO so the visual list still shows one card per SO
  const grouped = {};
  items.forEach(it => {
    const k = it.order_id;
    if (!grouped[k]) grouped[k] = { so_number: it.so_number, order_info: it.order_info, items: [] };
    grouped[k].items.push(it);
  });

  // ---- Selection helpers (for cross-SO same-customer billing) ----
  const selectedItems = items.filter(it => selected[it.id]);
  const selectedCustomers = [...new Set(
    selectedItems.map(it => (it.order_info?.customer_name || '').trim()).filter(Boolean)
  )];
  const selectedSoNumbers = [...new Set(selectedItems.map(it => it.so_number).filter(Boolean))];
  const selectionIsCrossSo = selectedSoNumbers.length > 1;
  const selectionIsMultiCustomer = selectedCustomers.length > 1;

  const handleMultiBill = (orderId) => {
    const orderItems = grouped[orderId].items.filter(it => selected[it.id]);
    if (orderItems.length < 1) { toast.error('Select at least one item from this SO'); return; }
    const init = {};
    orderItems.forEach(it => { init[it.id] = pendingToBill(it); });
    setBilledQtys(init);
    setBilling({
      multi: true,
      items: orderItems,
      so_number: orderItems[0].so_number,
      customer_name: orderItems[0].order_info?.customer_name || '',
      order_info: orderItems[0].order_info
    });
    setForm({
      tally_invoice_no: '',
      invoice_date: new Date().toISOString().split('T')[0],
      billed_qty: '',
      invoice_pdf_url: ''
    });
  };

  const handleCrossSoBill = () => {
    if (selectionIsMultiCustomer) {
      toast.error(`Cannot bill across customers — selected items belong to: ${selectedCustomers.join(', ')}. Please bill one customer at a time.`);
      return;
    }
    if (selectedItems.length < 1) { toast.error('Select at least one item'); return; }
    const init = {};
    selectedItems.forEach(it => { init[it.id] = pendingToBill(it); });
    setBilledQtys(init);
    setBilling({
      multi: true,
      crossSo: true,
      items: selectedItems,
      so_number: selectedSoNumbers.join(' + '),
      customer_name: selectedCustomers[0] || '',
      order_info: selectedItems[0]?.order_info,
    });
    setForm({
      tally_invoice_no: '',
      invoice_date: new Date().toISOString().split('T')[0],
      billed_qty: '',
      invoice_pdf_url: ''
    });
  };

  const selectedCountForSO = (orderId) => grouped[orderId].items.filter(it => selected[it.id]).length;

  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="flow-billing">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Billing</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Generate Tally invoices for ready-to-pack items</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Search SO #, item, our item name, customer..." value={search} onChange={e => setSearch(e.target.value)}
            className={`h-9 w-64 text-xs ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : ''}`} data-testid="billing-search-input" />
          <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)} data-testid="billing-company-filter">
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : items.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Receipt size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No items ready for billing</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([orderId, group]) => (
            <Card key={orderId} className={cardCls}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {group.so_number} <span className={`text-xs font-normal ml-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>— {group.order_info?.customer_name}</span>
                    </p>
                    <p className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{getFirmName(group.order_info?.company_id)} • {group.items.length} item(s) ready</p>
                  </div>
                  {selectedCountForSO(orderId) > 0 && (
                    <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 h-7 text-xs" onClick={() => handleMultiBill(orderId)} data-testid={`bill-multi-${orderId}`}>
                      <Receipt size={12} className="mr-1" /> Bill {selectedCountForSO(orderId)} as single invoice
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[720px]">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <th className={`w-8 py-2 px-2`}><input type="checkbox"
                          checked={group.items.every(it => selected[it.id])}
                          onChange={(e) => {
                            const ns = { ...selected };
                            group.items.forEach(it => { ns[it.id] = e.target.checked; });
                            setSelected(ns);
                          }}
                          data-testid={`select-all-${orderId}`}
                        /></th>
                        <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item</th>
                        <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty</th>
                        <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>HSN</th>
                        <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map(item => (
                        <tr key={item.id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50'}`}>
                          <td className="py-2 px-2"><input type="checkbox" checked={!!selected[item.id]} onChange={e => setSelected(s => ({ ...s, [item.id]: e.target.checked }))} data-testid={`select-${item.id}`} /></td>
                          <td className={`py-2 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <div className="font-medium">{item.item_name}</div>
                            {item.purchase_item_name && <div className="text-[9px] text-purple-400" title="Purchase team's item name">Purch: {item.purchase_item_name}</div>}
                            {item.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.description}</div>}
                            {item.admin_remarks && (
                              <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-amber-500 ${isDark ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'}`} title={`By ${item.admin_remarks_by || '-'}`}>
                                💬 <span className="font-medium">Admin:</span> {item.admin_remarks}
                              </div>
                            )}
                            {item.store_remarks && (
                              <div className={`mt-1 text-[10px] px-2 py-1 rounded border-l-2 border-teal-500 ${isDark ? 'bg-teal-500/10 text-teal-200' : 'bg-teal-50 text-teal-800'}`} title={`By ${item.store_remarks_by || 'Store'}`} data-testid={`store-remark-${item.id}`}>
                                📦 <span className="font-medium">Store:</span> {item.store_remarks}
                              </div>
                            )}
                          </td>
                          <td className={`py-2 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <div data-testid={`pending-qty-${item.id}`}>{pendingToBill(item)}</div>
                            {(item.quantity_billed || 0) > 0 && (
                              <div className={`text-[9px] font-normal ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                                {item.quantity_billed}/{item.quantity_allocated_to_bill} billed
                              </div>
                            )}
                          </td>
                          <td className={`py-2 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{item.hsn_code || '-'}</td>
                          <td className="py-2 px-2 text-right">
                            <Button size="sm" variant="ghost" className={`h-7 px-2 text-xs ${isDark ? 'text-cyan-400 hover:bg-cyan-500/10' : 'text-cyan-600 hover:bg-cyan-50'}`} onClick={() => openBilling(item)} data-testid={`bill-${item.id}`}>
                              Bill alone
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400 hover:bg-red-500/10"
                              onClick={async () => {
                                const reason = window.prompt(`Short-close remaining unbilled units of "${item.item_name}"?\n\nEnter reason (min 5 chars):`, '');
                                if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error('Valid reason required'); return; }
                                try { const r = await billingShortClose(item.id, reason.trim()); toast.success(r.data.message); fetchData(); }
                                catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}
                              data-testid={`billing-short-close-${item.id}`}>
                              Short Close
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!billing} onOpenChange={(v) => { if (!v) setBilling(null); }}>
        <DialogContent className={`max-w-lg max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {billing && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>
                  {billing.multi
                    ? (billing.crossSo
                        ? `Bill ${billing.items.length} items across ${billing.so_number.split(' + ').length} SOs`
                        : `Bill ${billing.items.length} items — ${billing.so_number}`)
                    : `Bill — ${billing.item_name}`}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                {billing.multi ? (
                  <div className={`text-xs p-2 rounded space-y-1 ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                    <div>
                      {billing.crossSo ? <>SOs: <b>{billing.so_number}</b></> : <>SO: <b>{billing.so_number}</b></>} | Customer: <b>{billing.customer_name || billing.order_info?.customer_name}</b>
                    </div>
                    <div className="border-t border-gray-200/20 pt-1 space-y-1">
                      {billing.items.map(it => (
                        <div key={it.id} className="flex items-center gap-2">
                          <span className="flex-1 truncate">
                            {billing.crossSo && <span className={`text-[10px] font-mono mr-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{it.so_number}</span>}
                            {it.item_name}
                          </span>
                          <span className={isDark ? 'text-white/40' : 'text-gray-500'}>pending: {pendingToBill(it)}</span>
                          <Input type="number" min="1" max={pendingToBill(it)}
                            className={`w-20 h-7 text-xs ${inputCls}`}
                            value={billedQtys[it.id] ?? pendingToBill(it)}
                            onChange={e => setBilledQtys(q => ({ ...q, [it.id]: e.target.value }))}
                            data-testid={`bill-qty-${it.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={`text-xs p-2 rounded ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-600'}`}>
                    SO: <b>{billing.so_number}</b> | Pending to Bill: <b>{pendingToBill(billing)}</b>
                    {(billing.quantity_billed || 0) > 0 && (
                      <span className={`ml-2 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                        (already billed {billing.quantity_billed} of allocated {billing.quantity_allocated_to_bill})
                      </span>
                    )}
                  </div>
                )}
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Tally Invoice No *</Label>
                  <Input className={`mt-1 ${inputCls}`} value={form.tally_invoice_no}
                    onChange={e => setForm(f => ({ ...f, tally_invoice_no: e.target.value }))}
                    required data-testid="billing-invoice-no" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={isDark ? 'text-white/60' : ''}>Invoice Date *</Label>
                    <Input type="date" className={`mt-1 ${inputCls}`} value={form.invoice_date}
                      onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} required />
                  </div>
                  {!billing.multi && (
                    <div>
                      <Label className={isDark ? 'text-white/60' : ''}>Billed Qty *</Label>
                      <Input type="number" min="1" max={pendingToBill(billing)} className={`mt-1 ${inputCls}`}
                        value={form.billed_qty} onChange={e => setForm(f => ({ ...f, billed_qty: e.target.value }))} required data-testid="billing-qty" />
                    </div>
                  )}
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Invoice PDF / Image *</Label>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/60 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      <Upload size={16} /> {form.invoice_pdf_url ? 'Replace' : 'Upload Invoice'}
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} data-testid="billing-upload" />
                    </label>
                    {form.invoice_pdf_url && <span className="text-green-400 text-xs flex items-center gap-1"><Check size={12} /> Uploaded</span>}
                  </div>
                  {/* Live invoice preview so the user can eyeball it before submitting */}
                  {form.invoice_pdf_url && (
                    <div className={`mt-2 rounded-lg border overflow-hidden ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`} data-testid="invoice-preview">
                      {(() => {
                        const url = form.invoice_pdf_url.startsWith('http') ? form.invoice_pdf_url : `${process.env.REACT_APP_BACKEND_URL}${form.invoice_pdf_url}`;
                        const isPdf = /\.pdf(\?|$)/i.test(form.invoice_pdf_url);
                        return isPdf ? (
                          <embed src={url} type="application/pdf" className="w-full h-64" />
                        ) : (
                          <img src={url} alt="Invoice preview" className="w-full max-h-64 object-contain" />
                        );
                      })()}
                      <div className={`flex items-center justify-between px-2 py-1 text-[10px] ${isDark ? 'bg-white/5 text-white/50' : 'bg-white text-gray-500'}`}>
                        <span>Preview — verify invoice # and amount match your Tally entry</span>
                        <a href={form.invoice_pdf_url.startsWith('http') ? form.invoice_pdf_url : `${process.env.REACT_APP_BACKEND_URL}${form.invoice_pdf_url}`} target="_blank" rel="noreferrer" className="text-blue-400 underline">Open full</a>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setBilling(null)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50" data-testid="billing-submit">
                    {submitting ? 'Billing...' : 'Confirm & Bill'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className={`flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
        <FileText size={16} className="text-cyan-400 flex-shrink-0 mt-0.5" />
        <p className={`text-xs ${isDark ? 'text-cyan-400/80' : 'text-cyan-700'}`}>
          After billing, items move to the Store queue for packing. The Store team will then push them to Logistics for dispatch.
        </p>
      </div>

      {/* Sticky cross-SO action bar — appears only when items spanning multiple SOs are selected */}
      {selectedItems.length > 0 && selectionIsCrossSo && (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-2xl w-[min(720px,calc(100vw-2rem))] rounded-2xl shadow-2xl border ${
          selectionIsMultiCustomer
            ? (isDark ? 'bg-red-900/80 border-red-500/50 backdrop-blur-lg' : 'bg-red-50 border-red-300')
            : (isDark ? 'bg-cyan-900/80 border-cyan-400/40 backdrop-blur-lg' : 'bg-cyan-50 border-cyan-300')
        }`} data-testid="cross-so-bar">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 sm:p-4">
            <div className="text-xs sm:text-sm">
              {selectionIsMultiCustomer ? (
                <div className={isDark ? 'text-red-200' : 'text-red-800'}>
                  <div className="font-bold">⚠ Multiple customers selected — cannot bill together</div>
                  <div className="text-[11px] mt-0.5">{selectedCustomers.join(' · ')}</div>
                </div>
              ) : (
                <div className={isDark ? 'text-cyan-100' : 'text-cyan-900'}>
                  <div className="font-bold">{selectedItems.length} items across {selectedSoNumbers.length} SOs</div>
                  <div className="text-[11px] mt-0.5">Customer: <b>{selectedCustomers[0]}</b> · SOs: {selectedSoNumbers.join(', ')}</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button size="sm" variant="ghost" onClick={() => setSelected({})} className={`h-8 px-3 text-xs ${isDark ? 'text-white/70 hover:bg-white/10' : 'text-gray-700 hover:bg-white'}`} data-testid="cross-so-clear">
                Clear
              </Button>
              <Button size="sm" onClick={handleCrossSoBill} disabled={selectionIsMultiCustomer} className="h-8 px-3 text-xs bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="cross-so-bill">
                <Receipt size={12} className="mr-1" /> Bill as single invoice
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowBilling;
